import React, { useDeferredValue, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowUturnLeftIcon, PencilSquareIcon, PlusIcon, TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { CheckCircleIcon, ExclamationTriangleIcon } from "@heroicons/react/20/solid";
import { Button } from "../forms/Buttons/Button";
import ShortEditableContent from "../KaTeXComponent/ShortEditableContent";
import { IMG_TAG, attrValue, hasForeignImage, isBlankHtml, isDocxImage, RichText } from "../KaTeXComponent/RichText";
import { DifficultyPicker } from "../Difficulty/Difficulty";
import { QuestionSubtopic } from "./ImportSubtopicPicker";

export { isBlankHtml, RichText };

// Warnings of the docx dry-run.
export const NOT_MARKED = "correct_answer_not_marked"; // no mark in the file: option A is saved as correct
export const NO_CORRECT = "no_correct_answer"; // no mark and no option A: saved without a correct answer
export const NO_OPTIONS = "no_answer_options";
// Parts of the file the import could not carry over exactly: shown under the question with what to check.
const IMPORT_NOTES = ["formula_skipped", "object_skipped", "pdf_table"];
export const hasWarning = (question, code) => !!question.warnings?.includes(code);
export const lacksOptions = (question) => hasWarning(question, NO_OPTIONS) || !question.answer_keys?.length;
// The file marked no correct answer: the one shown is only assumed until it is confirmed in the preview.
const assumedAnswer = (question) => hasWarning(question, NOT_MARKED) || hasWarning(question, NO_CORRECT);

// What a question needs to go to review in a subject, in the server's order (question_fields.<field>).
export const REQUIRED_FIELDS = ["subtopic", "difficulty", "body", "answer_keys", "correct_answer"];
const CONTENT_FIELDS = ["body", "answer_keys", "correct_answer"];

// What a question's content lacks there: the dry-run's `missing` for a question as parsed, the same rules
// here for one edited in the preview. A picture is content; an assumed correct answer (not marked in the
// file) is no answer until it is picked in the preview.
export const contentMissing = (q) => {
  if (!q.edited && Array.isArray(q.missing)) return q.missing.filter((field) => CONTENT_FIELDS.includes(field));
  const filled = (q.answer_keys || []).filter((o) => !isBlankHtml(o.body));
  const missing = [];
  if (isBlankHtml(q.body)) missing.push("body");
  if (filled.length < 2) missing.push("answer_keys");
  if (!filled.some((o) => o.is_correct) || assumedAnswer(q)) missing.push("correct_answer");
  return missing;
};

// The server's rules for an edited question; null when it would be accepted.
export const editProblem = (q) => {
  const keys = q.answer_keys || [];
  if (isBlankHtml(q.body)) return "my_questions.validation.body_required";
  if (keys.length < 2) return "my_questions.validation.min_two_answers";
  if (keys.some((o) => isBlankHtml(o.body))) return "my_questions.validation.answer_required";
  if (!keys.some((o) => o.is_correct)) return "my_questions.validation.correct_answer_required";
  if ([q.body, ...keys.map((o) => o.body)].some(hasForeignImage)) return "my_questions.upload.pasted_image";
  return null;
};

// data URI -> DOCX_n of the parsed images, to give images copied from the preview their reference back.
export const collectDocxRefs = (html, map) => {
  ((html || "").match(IMG_TAG) || []).forEach((tag) => {
    const ref = tag.match(/data-ref-key\s*=\s*["']?(DOCX_\w+)/i)?.[1];
    const src = attrValue(tag, "src");
    if (ref && src?.startsWith("data:")) map.set(src, ref);
  });
  return map;
};

// The same map from the dry-run's `images` ({DOCX_n: data URI}, sent with inline_images=false).
export const docxRefsFromImages = (images, map) => {
  Object.entries(images || {}).forEach(([ref, uri]) => {
    if (typeof uri === "string" && uri.startsWith("data:")) map.set(uri, ref);
  });
  return map;
};

// With inline_images=false the dry-run sends each docx image once, in `images`, and leaves
// <img data-ref-key="DOCX_n" src=""> in the HTML: put the image back, for display only.
export const withDocxImages = (html, images) => {
  if (!images || !html || !html.includes("DOCX_")) return html || "";
  return html.replace(IMG_TAG, (tag) => {
    const ref = tag.match(/data-ref-key\s*=\s*["']?(DOCX_\w+)/i)?.[1];
    const src = ref && images[ref];
    if (typeof src !== "string" || !src.startsWith("data:")) return tag;
    // In place, so dropping the data again (dropDocxImageData) gives back the original tag.
    const srcAttr = /(\ssrc\s*=\s*)(?:"[^"]*"|'[^']*'|[^\s>]+)/i;
    return srcAttr.test(tag)
      ? tag.replace(srcAttr, (_, name) => `${name}"${src}"`)
      : tag.replace(/^<img\b/i, () => `<img src="${src}"`);
  });
};

// The preview list renders without data-* attributes, so a copied image arrives as a bare data URI.
const restoreDocxRefs = (html, refByDataUri) =>
  (html || "").replace(IMG_TAG, (tag) => {
    if (isDocxImage(tag)) return tag;
    const ref = refByDataUri.get(attrValue(tag, "src") || "");
    if (!ref) return tag;
    return tag.replace(/\sdata-ref-key\s*=\s*(["'])\1/i, "").replace(/^<img\b/i, `<img data-ref-key="${ref}"`);
  });

// The editor wraps images in div.img-wrapper with a resize handle and forces display:block: keep the image.
const cleanEditorHtml = (html) => {
  if (!/img-wrapper|resize-handle/.test(html || "")) return html || "";
  const box = document.createElement("template");
  box.innerHTML = html;
  box.content.querySelectorAll(".resize-handle").forEach((el) => el.remove());
  box.content.querySelectorAll(".img-wrapper").forEach((wrapper) => {
    wrapper.querySelectorAll("img").forEach((img) => {
      img.style.removeProperty("display");
      if (!img.getAttribute("style")) img.removeAttribute("style");
    });
    wrapper.replaceWith(...wrapper.childNodes);
  });
  return box.innerHTML;
};

// Docx images travel by reference: the server re-reads the file and puts the stored image back for
// <img data-ref-key="DOCX_n">, so their data-URI bytes are not sent (multipart text fields are capped
// at 2.5 MB). Images uploaded in the editor keep their URL.
const dropDocxImageData = (html) =>
  (html || "").replace(IMG_TAG, (tag) =>
    isDocxImage(tag)
      ? tag
          .replace(/(\ssrc\s*=\s*)(?:"[^"]*"|'[^']*'|[^\s>]+)/i, '$1""')
          .replace(/\ssrcset\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/i, "")
      : tag
  );

// The server refuses NUL characters and lone surrogates (e.g. left by a broken paste).
// Valid pairs are matched first and kept. No lookbehind: Safari before 16.4 can't parse it, and a
// regex literal it can't parse breaks the whole bundle.
const wellFormed = (s) => {
  const fixed = s.toWellFormed
    ? s.toWellFormed()
    : s.replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]|[\uD800-\uDFFF]/g, (m) => (m.length === 2 ? m : "�"));
  return fixed.split("\u0000").join("");
};

// An edited question as the save request's `edits` entry.
export const toEditEntry = ({ index, name, body, answer_keys }) => ({
  index,
  name: wellFormed(name || ""),
  body: wellFormed(dropDocxImageData(body)),
  answer_keys: (answer_keys || []).map((o) => ({
    option: o.option,
    body: wellFormed(dropDocxImageData(o.body)),
    is_correct: !!o.is_correct,
  })),
});

const focusRing = "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600";
// Images are capped in height (aspect kept) so long files stay scannable.
const richTextClass = "min-w-0 text-sm text-slate-800 [&_img]:max-h-60 [&_img]:w-auto";
// Option labels A..Z, then AA, AB… (the server wants unique labels of at most 10 characters).
const letter = (i) => (i < 26 ? String.fromCharCode(65 + i) : letter(Math.floor(i / 26) - 1) + letter(i % 26));
const letterClass = (correct) =>
  `grid h-6 min-w-[1.5rem] shrink-0 place-items-center rounded-full px-1 text-xs font-semibold ${
    correct ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600"
  }`;
// Docx images compare by reference only: the editor shows them with their data URI, the dry-run may not.
const withoutDocxSrc = (html) =>
  (html || "").replace(IMG_TAG, (tag) =>
    isDocxImage(tag) ? tag.replace(/\ssrc\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/i, "") : tag
  );
const sameHtml = (x, y) => withoutDocxSrc(x) === withoutDocxSrc(y);
const sameContent = (a, b) => {
  const before = b.answer_keys || [];
  return (
    a.name === (b.name || "") &&
    sameHtml(a.body, b.body) &&
    a.answer_keys.length === before.length &&
    a.answer_keys.every(
      (o, i) => o.option === before[i].option && !!o.is_correct === !!before[i].is_correct && sameHtml(o.body, before[i].body)
    )
  );
};

function Badge({ tone, Icon = ExclamationTriangleIcon, children }) {
  const tones = {
    amber: "bg-amber-50 text-amber-800 ring-amber-600/20",
    red: "bg-red-50 text-red-700 ring-red-600/20",
    blue: "bg-blue-50 text-blue-700 ring-blue-600/20",
    slate: "bg-slate-100 text-slate-600 ring-slate-500/20",
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${tones[tone]}`}>
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {children}
    </span>
  );
}

// An answer option; with `onPick` the whole row (and its letter, for the keyboard) makes it the correct
// one. `confirmable`: the correct option was only assumed (not marked in the file), clicking confirms it.
export function PreviewOption({ option, onPick, confirmable }) {
  const [t] = useTranslation();
  const correct = !!option.is_correct;
  const clickable = onPick && (!correct || confirmable);
  const handleClick = (e) => {
    // Not for the second click of a double click, nor for the end of a text selection in this row.
    const selection = window.getSelection();
    if (e.detail > 1 || (selection && !selection.isCollapsed && e.currentTarget.contains(selection.anchorNode))) return;
    onPick();
  };
  return (
    <li
      onClick={onPick ? handleClick : undefined}
      // The whole row is the touch target for picking the answer: 44px at least on phones.
      className={`flex items-start gap-2 rounded-lg px-3 py-2 ring-1 ring-inset transition duration-200 ${
        correct ? "bg-emerald-50 ring-emerald-200" : "bg-white ring-slate-200"
      } ${clickable ? "cursor-pointer hover:bg-emerald-50/40 hover:ring-emerald-300" : ""} ${onPick ? "max-md:min-h-[2.75rem]" : ""}`}
    >
      {onPick ? (
        <button
          type="button"
          aria-pressed={correct}
          aria-label={t("my_questions.upload.mark_correct", { option: option.option })}
          className={`mt-0.5 ${letterClass(correct)} ${focusRing}`}
        >
          {option.option}
        </button>
      ) : (
        <span className={`mt-0.5 ${letterClass(correct)}`}>{option.option}</span>
      )}
      <div className={`flex-1 ${richTextClass}`}>
        <RichText html={option.body} />
      </div>
      {correct && (
        <>
          <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" aria-hidden="true" />
          <span className="sr-only">{t("my_questions.upload.correct")}</span>
        </>
      )}
    </li>
  );
}

// Problems the question already has when the editor opens (e.g. a parsed question with one option).
const initialErrors = (t, question) => {
  const problem = editProblem(question);
  if (!problem || problem === "my_questions.validation.correct_answer_required") return {};
  return problem === "my_questions.validation.body_required" ? { body: t(problem) } : { options: t(problem) };
};

// Inline editor of one previewed question (the server's rules: text, 2+ filled options, a correct one,
// no pasted image data). onApply gets the edited question; nothing is sent until the import is saved.
function QuestionEditor({ question, images, refByDataUri, onApply, onCancel }) {
  const [t] = useTranslation();
  const rootRef = useRef(null);
  const nameRef = useRef(null);
  const nextKey = useRef(0);
  const withKey = (option) => ({ ...option, key: nextKey.current++ });
  const [name, setName] = useState(question.name || "");
  // The editor shows docx images, so it starts from the HTML with their data URIs put back.
  const [initialBody] = useState(() => withDocxImages(question.body || "", images));
  const [body, setBody] = useState(initialBody);
  // A question without options gets two empty ones to fill in.
  const [options, setOptions] = useState(() =>
    (question.answer_keys?.length ? question.answer_keys : [{}, {}]).map((o) =>
      withKey({ body: withDocxImages(o.body || "", images), is_correct: !!o.is_correct })
    )
  );
  const [errors, setErrors] = useState(() => initialErrors(t, question));
  // Image uploads in flight: applying now would lose the image.
  const [uploads, setUploads] = useState(0);
  // The formula preview may lag behind typing instead of blocking it.
  const previewBody = useDeferredValue(body);

  // Keyboard users land in the editor, which may open below the visible part of the list.
  useEffect(() => {
    nameRef.current?.focus({ preventScroll: true });
    rootRef.current?.closest("li")?.scrollIntoView({ block: "nearest" });
  }, []);

  const updateOptions = (update) => {
    setOptions(update);
    setErrors((prev) => (prev.options ? { ...prev, options: "" } : prev));
  };
  const trackUpload = (uploading) => setUploads((n) => Math.max(0, n + (uploading ? 1 : -1)));
  const prepare = (html) => restoreDocxRefs(cleanEditorHtml(html), refByDataUri);

  const apply = () => {
    const nextBody = prepare(body);
    const nextOptions = options.map((o) => ({ ...o, body: prepare(o.body) }));
    const next = {};
    if (isBlankHtml(nextBody)) next.body = t("my_questions.validation.body_required");
    else if (hasForeignImage(nextBody)) next.body = t("my_questions.upload.pasted_image");
    if (nextOptions.length < 2) next.options = t("my_questions.validation.min_two_answers");
    else if (nextOptions.some((o) => isBlankHtml(o.body))) next.options = t("my_questions.validation.answer_required");
    else if (nextOptions.some((o) => hasForeignImage(o.body))) next.options = t("my_questions.upload.pasted_image");
    else if (!nextOptions.some((o) => o.is_correct)) next.options = t("my_questions.validation.correct_answer_required");
    setErrors(next);
    if (Object.keys(next).length) return;
    const edited = {
      ...question,
      // An emptied name falls back to the one the file gave (the server would generate the same).
      name: name.trim() || question.defaultName || "",
      body: nextBody,
      answer_keys: nextOptions.map((o, i) => ({ option: letter(i), body: o.body, is_correct: o.is_correct })),
    };
    if (sameContent(edited, question)) {
      // Nothing changed, but applying an assumed answer as shown confirms it, as picking it in the list
      // does (the import's notes on the unchanged content stay).
      if (assumedAnswer(question)) {
        const warnings = question.warnings.filter((w) => w !== NOT_MARKED && w !== NO_CORRECT);
        onApply({ ...edited, warnings, edited: true });
      } else {
        onCancel();
      }
      return;
    }
    onApply({ ...edited, warnings: [], edited: true });
  };

  return (
    <div
      ref={rootRef}
      className="mt-3 space-y-4 rounded-xl border border-blue-200 bg-blue-50/40 p-3 sm:p-4"
      onKeyDown={(e) => {
        // Esc cancels this edit only; the dialog ignores keydowns that are already handled.
        if (e.key === "Escape") {
          e.preventDefault();
          onCancel();
        }
      }}
    >
      <label className="block">
        <span className="mb-1.5 block text-sm font-medium text-slate-700">{t("my_questions.editor.name")}</span>
        <input
          ref={nameRef}
          type="text"
          maxLength={1000}
          className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none transition duration-200 placeholder:text-slate-400 hover:border-slate-400 focus:border-blue-600 focus:ring-4 focus:ring-blue-600/15"
          placeholder={question.defaultName || t("my_questions.editor.name_placeholder")}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </label>

      <div>
        <span className="mb-1.5 block text-sm font-medium text-slate-700">{t("my_questions.editor.question_label")}</span>
        <ShortEditableContent
          value={initialBody}
          ariaLabel={t("my_questions.upload.question_text")}
          onUploadingChange={trackUpload}
          onChange={(html) => {
            setBody(html);
            setErrors((prev) => (prev.body ? { ...prev, body: "" } : prev));
          }}
        />
        {errors.body && (
          <span role="alert" className="mt-1 block text-xs font-medium text-red-600">
            {errors.body}
          </span>
        )}
        <div className={`mt-2 rounded-lg border border-dashed border-slate-200 bg-white px-3 py-2 ${richTextClass}`}>
          <RichText html={cleanEditorHtml(previewBody)} />
        </div>
      </div>

      <div>
        <span className="mb-1.5 block text-sm font-medium text-slate-700">{t("my_questions.editor.answer_options")}</span>
        <div className="space-y-2">
          {options.map((o, i) => (
            <div key={o.key} className="flex items-start gap-2">
              <button
                type="button"
                onClick={() => updateOptions((prev) => prev.map((p) => ({ ...p, is_correct: p.key === o.key })))}
                aria-pressed={o.is_correct}
                aria-label={t("my_questions.upload.mark_correct", { option: letter(i) })}
                // Phones: 44px touch targets, as the preview's other controls.
                className={`mt-2 grid h-7 min-w-[1.75rem] shrink-0 place-items-center rounded-full px-1 text-xs font-semibold transition duration-200 max-md:mt-0 max-md:h-11 max-md:min-w-[2.75rem] ${focusRing} ${
                  o.is_correct
                    ? "bg-emerald-600 text-white shadow-sm shadow-emerald-600/25"
                    : "bg-white text-slate-600 ring-1 ring-inset ring-slate-300 hover:bg-emerald-50 hover:text-emerald-700 hover:ring-emerald-300"
                }`}
              >
                {letter(i)}
              </button>
              <div className="min-w-0 flex-1">
                <ShortEditableContent
                  value={o.body}
                  ariaLabel={t("my_questions.upload.option_label", { option: letter(i) })}
                  onUploadingChange={trackUpload}
                  onChange={(html) => updateOptions((prev) => prev.map((p) => (p.key === o.key ? { ...p, body: html } : p)))}
                />
              </div>
              {options.length > 2 && (
                <button
                  type="button"
                  onClick={() => updateOptions((prev) => prev.filter((p) => p.key !== o.key))}
                  aria-label={t("my_questions.upload.remove_option", { option: letter(i) })}
                  title={t("buttons.remove")}
                  className={`mt-1.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg text-slate-400 transition duration-200 hover:bg-red-50 hover:text-red-600 max-md:mt-0 max-md:h-11 max-md:w-11 ${focusRing}`}
                >
                  <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              )}
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={() => updateOptions((prev) => [...prev, withKey({ body: "", is_correct: false })])}
          className={`-ml-2 mt-2 inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-medium text-blue-600 transition duration-200 hover:bg-blue-50 hover:text-blue-700 max-md:min-h-[2.75rem] ${focusRing}`}
        >
          <PlusIcon className="h-4 w-4" aria-hidden="true" />
          {t("my_questions.editor.add_option")}
        </button>
        {errors.options && (
          <span role="alert" className="mt-1 block text-xs font-medium text-red-600">
            {errors.options}
          </span>
        )}
      </div>

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" className="shrink-0 max-sm:h-11" text={t("buttons.cancel")} color="secondary-outline" onClick={onCancel} />
        <Button
          type="button"
          className="shrink-0 max-sm:h-11"
          text={t("my_questions.upload.apply_edit")}
          color="primary"
          onClick={apply}
          disabled={uploads > 0}
        />
      </div>
    </div>
  );
}

/**
 * One question of the dry-run preview: number, name, body, options with the correct one marked, warnings.
 * With `editable` (the backend accepts edits), clicking an option makes it the only correct one
 * (onPickCorrect(question, option)), "Edit" opens the inline editor (onStartEdit(index) →
 * onApplyEdit(question) / onCancelEdit()) and an edited question can be reverted (onRevert(index), the link
 * next to its "Edited" mark).
 * With `removable` (the backend skips indexes), the trash icon ("Delete") leaves a question parsed wrong out of
 * the import (onRemove(index)); a `removed` question shrinks to one line with "Restore" (onRestore(index)).
 * With `onDifficulty` (the backend accepts difficulty), A / B / C sets the question's `difficulty`
 * (onDifficulty(index, level | null)).
 * With `onSubtopic` (the backend takes `subtopics` and the target subject is known), the question picks a
 * subtopic of `catalog` (the subject's topics) as its own `subtopic`, or keeps the import's `generalSubtopic`
 * (onSubtopic(index, subtopic | null)); `subtopicRequired` marks a missing one, `subtopicInvalid` one the
 * server refused; `grade` narrows its topics to that grade's.
 * `strict` (a subject's import: only complete questions are saved): an assumed correct answer is not taken.
 * `missing` ("subtopic,difficulty", or "") lists what the question still lacks; `rejected`: the server
 * refused it on save (shown in red).
 * `domId` makes the item a target the dialog can scroll to and focus.
 * `locked` (another question is being edited, or the import is being saved) disables those controls.
 * `images` ({DOCX_n: data URI}) fills in the docx images the dry-run sent separately.
 * Memoized, and its rich text is memoized too: the dialog's own state changes must stay cheap.
 */
const PreviewQuestion = React.memo(function PreviewQuestion({
  question,
  images,
  editable,
  removable,
  removed,
  locked,
  isEditing,
  refByDataUri,
  onPickCorrect,
  onStartEdit,
  onRevert,
  onApplyEdit,
  onCancelEdit,
  onRemove,
  onRestore,
  difficulty = null,
  onDifficulty = null,
  domId,
  strict = false,
  missing = "",
  rejected = false,
  catalog = null,
  subtopic = null,
  generalSubtopic = null,
  subtopicRequired = false,
  subtopicInvalid = false,
  onSubtopic = null,
  grade = null,
}) {
  const [t] = useTranslation();
  const options = question.answer_keys || [];
  const editButtonRef = useRef(null);
  const removeButtonRef = useRef(null);
  const restoreButtonRef = useRef(null);
  const wasEditing = useRef(isEditing);
  const wasRemoved = useRef(removed);
  // "Undo changes" was pressed here: it goes with the "Edited" mark, so the focus needs a new place.
  const reverting = useRef(false);
  const interactive = editable && !locked;

  // Back to the Edit button when the editor closes, so keyboard focus is not lost.
  useEffect(() => {
    if (wasEditing.current && !isEditing) editButtonRef.current?.focus({ preventScroll: true });
    wasEditing.current = isEditing;
  }, [isEditing]);

  // The same after "Undo changes", which disappears with the edit it undid.
  useEffect(() => {
    if (!question.edited && reverting.current) {
      reverting.current = false;
      editButtonRef.current?.focus({ preventScroll: true });
    }
  }, [question.edited]);

  // Delete and Restore replace each other: focus moves to the one that appeared.
  useEffect(() => {
    if (wasRemoved.current !== removed) {
      (removed ? restoreButtonRef : removeButtonRef).current?.focus({ preventScroll: true });
    }
    wasRemoved.current = removed;
  }, [removed]);

  // Delete and Restore take each other's place, so the second click of a double click would undo the first.
  const handleRemove = (e) => {
    if (e.detail <= 1) onRemove(question.index);
  };
  const handleRestore = (e) => {
    if (e.detail <= 1) onRestore(question.index);
  };

  // A jump from the dialog's summary focuses the whole item; the ring shows where it landed.
  const anchorProps = { id: domId, tabIndex: domId ? -1 : undefined };
  const anchorClass = "rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-4";
  const missingFields = missing ? missing.split(",") : [];

  if (removed) {
    return (
      <li {...anchorProps} className={`py-3 first:pt-0 last:pb-0 ${anchorClass}`}>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-sm font-semibold tabular-nums text-slate-500">{question.index}.</span>
          <span className="min-w-0 break-words text-sm font-medium text-slate-500 line-through">
            {question.name || question.defaultName}
          </span>
          <Badge tone="slate" Icon={TrashIcon}>
            {t("my_questions.upload.removed_badge")}
          </Badge>
          <div className="ml-auto">
            <button
              ref={restoreButtonRef}
              type="button"
              onClick={handleRestore}
              disabled={locked}
              aria-label={t("my_questions.upload.restore_question", { index: question.index })}
              className={`inline-flex min-h-[2.75rem] items-center gap-1 whitespace-nowrap rounded-lg px-2.5 text-sm font-medium text-slate-600 transition duration-200 enabled:hover:bg-slate-100 enabled:hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-40 md:h-8 md:min-h-0 ${focusRing}`}
            >
              <ArrowUturnLeftIcon className="h-4 w-4" aria-hidden="true" />
              {t("my_questions.upload.restore")}
            </button>
          </div>
        </div>
      </li>
    );
  }

  return (
    <li {...anchorProps} className={`py-4 first:pt-0 last:pb-0 ${anchorClass}`}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className={`text-sm font-semibold tabular-nums ${rejected ? "text-red-600" : "text-slate-400"}`}>{question.index}.</span>
        {!isEditing && (
          <span className="min-w-0 break-words text-sm font-semibold text-slate-900">{question.name || question.defaultName}</span>
        )}
        {question.edited && (
          <Badge tone="blue" Icon={PencilSquareIcon}>
            {t("my_questions.upload.edited_badge")}
          </Badge>
        )}
        {/* Undoing the edit goes with the mark it undoes, not with the row's actions. */}
        {editable && question.edited && !isEditing && (
          <button
            type="button"
            onClick={() => {
              reverting.current = true;
              onRevert(question.index);
            }}
            disabled={locked}
            aria-label={t("my_questions.upload.revert_question", { index: question.index })}
            className={`inline-flex items-center gap-1 whitespace-nowrap rounded-md px-1.5 py-0.5 text-xs font-medium text-blue-700 transition duration-200 enabled:hover:bg-blue-50 enabled:hover:text-blue-800 disabled:cursor-not-allowed disabled:opacity-40 max-md:min-h-[2.75rem] ${focusRing}`}
          >
            <ArrowUturnLeftIcon className="h-3.5 w-3.5" aria-hidden="true" />
            {t("my_questions.upload.revert")}
          </button>
        )}
        {/* In a subject the assumed answer is not taken: it has to be picked (confirmed) here. */}
        {hasWarning(question, NOT_MARKED) && (
          <Badge tone="amber">
            {strict ? t("my_questions.upload.incomplete.not_marked_badge") : t("my_questions.upload.not_marked_badge")}
          </Badge>
        )}
        {hasWarning(question, NO_CORRECT) && <Badge tone="red">{t("my_questions.upload.no_correct_badge")}</Badge>}
        {lacksOptions(question) && <Badge tone="red">{t("my_questions.upload.no_options_badge")}</Badge>}
        {(editable || removable || onDifficulty) && !isEditing && (
          <div className="ml-auto flex flex-wrap items-center justify-end gap-1">
            {onDifficulty && (
              <DifficultyPicker
                compact
                value={difficulty}
                onChange={(level) => onDifficulty(question.index, level)}
                disabled={locked}
                // Phones: the picker's buttons are 44px touch targets, as the row's other buttons.
                className={`max-md:[&>button]:h-11 max-md:[&>button]:min-w-[2.75rem] ${editable || removable ? "mr-1" : ""}`}
              />
            )}
            {editable && (
              <button
                ref={editButtonRef}
                type="button"
                onClick={() => onStartEdit(question.index)}
                disabled={locked}
                aria-label={t("my_questions.upload.edit_question", { index: question.index })}
                className={`inline-flex min-h-[2.75rem] items-center gap-1 whitespace-nowrap rounded-lg px-2.5 text-sm font-medium text-slate-600 transition duration-200 enabled:hover:bg-slate-100 enabled:hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-40 md:h-8 md:min-h-0 ${focusRing}`}
              >
                <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
                {t("buttons.edit")}
              </button>
            )}
            {/* Leaving a question out is the rarer action: a quiet icon, named for screen readers and in its tooltip. */}
            {removable && (
              <button
                ref={removeButtonRef}
                type="button"
                onClick={handleRemove}
                disabled={locked}
                aria-label={t("my_questions.upload.remove_question", { index: question.index })}
                title={t("buttons.delete")}
                className={`inline-flex h-11 w-11 items-center justify-center rounded-lg text-slate-500 transition duration-200 enabled:hover:bg-red-50 enabled:hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40 md:h-8 md:w-8 ${focusRing}`}
              >
                <TrashIcon className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
          </div>
        )}
      </div>
      {IMPORT_NOTES.filter((code) => hasWarning(question, code)).map((code) => (
        <p key={code} className="mt-1.5 flex items-start gap-1.5 text-xs text-amber-800">
          <ExclamationTriangleIcon className="mt-px h-3.5 w-3.5 shrink-0 text-amber-500" aria-hidden="true" />
          {t(`my_questions.upload.warnings.${code}`)}
        </p>
      ))}
      {onSubtopic && catalog && (
        <QuestionSubtopic
          index={question.index}
          catalog={catalog}
          own={subtopic}
          general={generalSubtopic}
          grade={grade}
          required={subtopicRequired}
          invalid={subtopicInvalid}
          disabled={locked}
          onChange={onSubtopic}
        />
      )}
      {missingFields.length > 0 && (
        <p className={`mt-1.5 flex items-start gap-1.5 text-xs font-medium ${rejected ? "text-red-700" : "text-amber-800"}`}>
          <ExclamationTriangleIcon
            className={`mt-px h-3.5 w-3.5 shrink-0 ${rejected ? "text-red-500" : "text-amber-500"}`}
            aria-hidden="true"
          />
          {t("my_questions.upload.incomplete.question", {
            fields: missingFields.map((field) => t(`question_fields.${field}`, { defaultValue: field })).join(", "),
          })}
        </p>
      )}
      {isEditing ? (
        <QuestionEditor
          question={question}
          images={images}
          refByDataUri={refByDataUri}
          onApply={onApplyEdit}
          onCancel={onCancelEdit}
        />
      ) : (
        <>
          <div className={`mt-2 ${richTextClass}`}>
            <RichText html={withDocxImages(question.body, images)} />
          </div>
          {options.length > 0 && (
            <ul className="mt-3 space-y-1.5">
              {options.map((option, i) => (
                <PreviewOption
                  key={option.option || i}
                  option={images ? { ...option, body: withDocxImages(option.body, images) } : option}
                  confirmable={hasWarning(question, NOT_MARKED)}
                  onPick={interactive ? () => onPickCorrect(question, option.option) : null}
                />
              ))}
            </ul>
          )}
        </>
      )}
    </li>
  );
});

export default PreviewQuestion;
